import React, { useState, useEffect } from 'react';
import { PencilIcon, CheckIcon, TrophyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const EarningsGoalTracker = ({ currentEarnings = 0 }) => {
    const [goal, setGoal] = useState(50000); // Default goal 50,000 Yen
    const [isEditing, setIsEditing] = useState(false);
    const [inputGoal, setInputGoal] = useState('');

    // Save/Load goal from localStorage
    useEffect(() => {
        const savedGoal = localStorage.getItem('monthly_earnings_goal');
        if (savedGoal) {
            setGoal(parseInt(savedGoal, 10));
        }
    }, []);

    const handleSaveGoal = () => {
        const newGoal = parseInt(inputGoal, 10);
        if (isNaN(newGoal) || newGoal < 0) {
            toast.error('有効な金額を入力してください');
            return;
        }
        setGoal(newGoal);
        localStorage.setItem('monthly_earnings_goal', newGoal);
        setIsEditing(false);
        toast.success('今月の目標を設定しました！');
    };

    const startEditing = () => {
        setInputGoal(goal.toString());
        setIsEditing(true);
    };

    const progress = Math.min((currentEarnings / goal) * 100, 100);
    const remaining = Math.max(goal - currentEarnings, 0);
    const isAchieved = currentEarnings >= goal;

    // Motivational messages
    const getMotivationalMessage = () => {
        if (isAchieved) return '🎉 目標達成おめでとうございます！素晴らしい成果です！';
        if (progress >= 80) return '🔥 あと少し！目標達成までラストスパート！';
        if (progress >= 50) return '👍 折り返し地点です！この調子で頑張りましょう。';
        if (progress >= 20) return '🚀 良いスタートです！コツコツ積み上げましょう。';
        return '🌱 今月の目標に向けて、最初の一歩を踏み出そう！';
    };

    return (
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-lg p-6 text-white shadow-lg mb-8 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white opacity-10 rounded-full blur-xl"></div>
            <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-20 h-20 bg-yellow-400 opacity-10 rounded-full blur-xl"></div>

            <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <TrophyIcon className="w-6 h-6 text-yellow-300" />
                        <h2 className="text-lg font-bold">今月の目標</h2>
                    </div>
                    {!isEditing && (
                        <button
                            onClick={startEditing}
                            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                        >
                            <PencilIcon className="w-4 h-4 text-white opacity-80" />
                        </button>
                    )}
                </div>

                <div className="mb-4">
                    {isEditing ? (
                        <div className="flex items-center space-x-2">
                            <span className="text-2xl font-bold">¥</span>
                            <input
                                type="number"
                                value={inputGoal}
                                onChange={(e) => setInputGoal(e.target.value)}
                                className="w-full text-2xl font-bold bg-transparent border-b-2 border-white focus:outline-none placeholder-blue-200"
                                autoFocus
                            />
                            <button
                                onClick={handleSaveGoal}
                                className="bg-white text-blue-600 p-2 rounded-full hover:bg-blue-50 transition-colors"
                            >
                                <CheckIcon className="w-5 h-5" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-sm text-blue-100 mb-1">現在の獲得金額</p>
                                <div className="text-3xl font-bold">¥{currentEarnings.toLocaleString()}</div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-blue-100 mb-1">目標金額</p>
                                <div className="text-xl font-semibold opacity-90">/ ¥{goal.toLocaleString()}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-black bg-opacity-20 rounded-full h-3 mb-3">
                    <div
                        className="bg-yellow-400 h-3 rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                {/* Message */}
                <div className="bg-white bg-opacity-10 rounded-lg p-3 text-sm font-medium border border-white border-opacity-10 backdrop-blur-sm">
                    {isEditing ? (
                        '🎯 新しい目標金額を入力してください'
                    ) : (
                        <>
                            {getMotivationalMessage()}
                            {!isAchieved && (
                                <div className="mt-1 text-xs opacity-80">
                                    あと <span className="font-bold">¥{remaining.toLocaleString()}</span> で達成！
                                    <span className="ml-1 opacity-75">
                                        (時給¥1,200なら約{Math.ceil(remaining / 1200)}時間)
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EarningsGoalTracker;
